/**
 * /advertisers/sub-users — multi-user advertiser workspace (feature #143).
 *
 * Declares the TanStack Router route and delegates rendering to
 * `AdvertiserSubUsersPage`. The flat-file naming convention uses dots as path
 * separators, so `advertisers.sub-users.tsx` maps to `/advertisers/sub-users`.
 *
 * Follows the same shape as `advertisers.portal.tsx` so the central wire-up
 * can mount it by importing this module.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AdvertiserSubUsersPage } from "@/features/advertiser-sub-users/AdvertiserSubUsersPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/advertisers/sub-users`. Optional `advertiserId` query
// param lets deep links select a workspace.
// ---------------------------------------------------------------------------

interface AdvertiserSubUsersSearch {
	advertiserId?: string;
}

export const advertisersSubUsersRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/advertisers/sub-users",
	validateSearch: (raw: Record<string, unknown>): AdvertiserSubUsersSearch => {
		const advertiserId = raw.advertiserId;
		if (typeof advertiserId === "string" && advertiserId.trim().length > 0) {
			return { advertiserId: advertiserId.trim() };
		}
		return {};
	},
	component: SubUsersRouteComponent,
});

export default advertisersSubUsersRoute;

// ---------------------------------------------------------------------------
// Adapter — pulls the `advertiserId` search param and forwards to the page.
// ---------------------------------------------------------------------------

function SubUsersRouteComponent() {
	const search = advertisersSubUsersRoute.useSearch() as AdvertiserSubUsersSearch;
	return <AdvertiserSubUsersPage advertiserId={search.advertiserId} />;
}

// Re-export so the router can `import { AdvertiserSubUsersPage }` from this
// module if the central wire-up prefers a single-source import.
export { AdvertiserSubUsersPage } from "@/features/advertiser-sub-users/AdvertiserSubUsersPage";
